/**
 * Formats a date as relative time (e.g., "2 hours ago", "3 days ago")
 * Replacement for moment().fromNow()
 */
export function timeAgo(date: Date | string | number): string {
  const now = Date.now();
  const timestamp = new Date(date).getTime();
  const seconds = Math.floor((now - timestamp) / 1000);

  if (seconds < 60) {
    return 'just now';
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return minutes === 1 ? 'a minute ago' : `${minutes} minutes ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return hours === 1 ? 'an hour ago' : `${hours} hours ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 30) {
    return days === 1 ? 'a day ago' : `${days} days ago`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return months === 1 ? 'a month ago' : `${months} months ago`;
  }

  const years = Math.floor(months / 12);
  return years === 1 ? 'a year ago' : `${years} years ago`;
}

/**
 * Returns the difference between two dates in the specified unit
 * Replacement for moment().diff(other, unit)
 */
export function dateDiff(
  date1: Date | string | number,
  date2: Date | string | number,
  unit: 'seconds' | 'minutes' | 'hours' | 'days',
): number {
  const ms = new Date(date1).getTime() - new Date(date2).getTime();
  switch (unit) {
    case 'seconds':
      return Math.floor(ms / 1000);
    case 'minutes':
      return Math.floor(ms / (1000 * 60));
    case 'hours':
      return Math.floor(ms / (1000 * 60 * 60));
    case 'days':
      return Math.floor(ms / (1000 * 60 * 60 * 24));
  }
}

/**
 * Formats a date as "MMM YYYY" (e.g., "Jan 2024")
 * Replacement for moment().format('MMM YYYY')
 */
export function formatMonthYear(date: Date | string | number): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
}
